import {UILobbyTableRowController} from './UILobbyTableRowController.ts'
import {SocketLobbySend} from '../../socket/SocketLobbySend.ts'
import {SocketDataTypes} from '../../../../../shared/SocketDataTypes.ts'
import {UILobbyTableHeaderController} from './UILobbyTableHeaderController.ts'
import ILobbyGameData = SocketDataTypes.LobbyGameData
import {GameSorter} from '../../../../../shared/GameSorter.ts'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints.ts'
import {logError} from '../../ui_utils/Utils.ts'
import {createGloballyExclusiveHandler} from '../../ui_utils/ClientUtils.ts'
import {UILobbyRoomListController} from './UILobbyRoomListController.ts'
import {type NetworkEnums} from '../../../../../shared/GameEnums.ts'
import {uiManagerEvents} from '../../EntryShared.ts'

type GameClickAction = (gameId: string) => void

export class UILobbyTableController {
    readonly headerController: UILobbyTableHeaderController

    private readonly spectateTableBody: HTMLElement
    private spectateRows: UILobbyTableRowController[] = []

    private readonly gameSorter = new GameSorter()

    private readonly kickedFromRooms: Set<string> = new Set<string>()
    private roomList: UILobbyRoomListController | undefined

    private visible: boolean = false

    constructor() {
        this.headerController = new UILobbyTableHeaderController(() => this.refreshGames(), () => this.updateRoomListDisplay())
        this.spectateTableBody = document.getElementById('lobby_center_spectate_table_body') as HTMLElement
    }

    show() {
        this.visible = true

        this.updateRoomListDisplay()
    }

    hide() {
        this.visible = false

        this.updateRoomListDisplay()
    }

    updateRoomList(data: NetworkEnums.RoomListUpdateData) {
        this.roomList?.update(data)
    }

    onGotKicked(roomId: string, canRejoin: boolean) {
        if(canRejoin) return

        this.kickedFromRooms.add(roomId)
        this.roomList?.setRoomUnavailable(roomId)
    }

    private updateRoomListDisplay() {
        this.roomList?.destroy()
        this.roomList = undefined
        if(this.visible && this.headerController.isRoomListActive()) {
            const tableBody = document.getElementById('lobby_center_rooms_table_body') as HTMLElement
            const header = document.getElementById('lobby_center_table_rooms_tab_div') as HTMLElement
            this.roomList = new UILobbyRoomListController(tableBody, header, UILobbyTableController.createClickedJoinRoomCallback(), this.kickedFromRooms)
            this.roomList.activate()
        }
    }

    private static createClickedJoinRoomCallback() {
        return createGloballyExclusiveHandler(async(gameId: string) => {
            await uiManagerEvents.createClickedJoinRoomCallback(gameId)
        })
    }

    async refreshGames() {
        this.headerController.updateLastRefreshTimestamp()

        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetGameList(), undefined, undefined, false)
        if(data === undefined) {
            return logError('refreshGames', ['Network error'])
        }

        this.updateSpectateGames(data.json)
    }

    private updateSpectateGames(newData: ILobbyGameData) {
        this.headerController.showActiveTableBody()
        UILobbyTableController.removeLobbyTableRows(this.spectateRows)
        const sortedLobbyRowData = this.gameSorter.sort(newData.games)
        this.spectateRows = this.createLobbyTableRows(sortedLobbyRowData, this.spectateTableBody, UILobbyTableController.createClickedSpectateRoomCallback())
    }

    private static createClickedSpectateRoomCallback() {
        return createGloballyExclusiveHandler(async(gameId: string) => {
            await SocketLobbySend.clickedSpectateGame(gameId)
        })
    }

    private static removeLobbyTableRows(rows: UILobbyTableRowController[]) {
        rows.forEach((row) => {
            row.remove()
        })
        rows.length = 0
    }

    // eslint-disable-next-line class-methods-use-this
    private createLobbyTableRows(lobbyRowData: SocketDataTypes.LobbyRowData[], container: HTMLElement, clickAction: GameClickAction): UILobbyTableRowController[] {
        const rows: UILobbyTableRowController[] = []
        for(const rowItem of lobbyRowData) {
            const lobbyRow = new UILobbyTableRowController(container, rowItem, clickAction)
            lobbyRow.update(rowItem)
            rows.push(lobbyRow)
        }
        return rows
    }
}
